<template>
  <div>
    <!-- Base Modal - Used for both Initial and Final states -->
    <BaseModal
      ref="baseModal"
      :modal-id="modalId"
      :modal-text="modalText"
      :button-text="buttonText"
      :button-full-width="currentState === 'final'"
      @button-click="handleBaseModalAction"
      v-if="isBaseModalVisible"
    />

    <!-- Job Description Modal -->
    <JobDescriptionModal
      ref="jobModal"
      v-if="isJobModalVisible"
      :currentJob="currentJob"
      :currentIndex="currentJobIndex"
      :totalJobs="jobs.length"
      :loading="loading"
      :company="company"
      :isInitialScout="true"
      :errorMessage="errorMessage"
      :isJobScouted="isCurrentJobScouted"
      :hasReachedScoutLimit="hasReachedScoutLimit"
      :hasCompanyActiveScout="hasCompanyActiveScout"
      @prev="handlePrevJob"
      @next="handleNextJob"
      @scout="openScoutConfirmation"
      @close="handlePopupClose"
    />

    <!-- Scout Confirmation Modal -->
    <ScoutConfirmationModal
      v-if="showScoutConfirmation"
      @confirm="handleScoutConfirm"
      @cancel="handleScoutCancel"
      ref="scoutConfirmModal"
      :loading="loading"
    />

    <!-- Non-registered company: same registration flow as JobLists -->
    <CompanySelectionModal
      ref="companySelectionModal"
      :companyName="(currentJob && currentJob.companyName) || ''"
      :companyId="(currentJob && (currentJob.company_id || currentJob.companyId)) || ''"
      :companyDescription="(selectedCompany && selectedCompany.short_description) || ''"
      :loading="loading"
      @proceed="handleCompanySelectionProceed"
      @cancel="handleCompanySelectionCancel"
    />

    <TermsConditionsModal
      ref="termsConditionsModal"
      @agree="handleTermsAgree"
      @cancel="handleTermsCancel"
    />
  </div>
</template>

<script>
import Api from "@/config/api";
import BaseModal from "./BaseModal.vue";
import JobDescriptionModal from "./JobDescriptionModal.vue";
import ScoutConfirmationModal from "./ScoutConfirmationModal.vue";
import CompanySelectionModal from "./CompanySelectionModal.vue";
import TermsConditionsModal from "./TermsConditionsModal.vue";
import { mapGetters } from "vuex";
export default {
  name: "ScoutJob",
  components: {
    BaseModal,
    JobDescriptionModal,
    ScoutConfirmationModal,
    CompanySelectionModal,
    TermsConditionsModal,
  },
  data() {
    return {
      // Modal States: 'jobs' | 'scoutSuccess' | 'final' | 'done'
      currentState: "jobs",

      // Job Data
      jobs: [], // Array of 3 jobs
      currentJobIndex: 0,
      scoutedJobs: [], // Track scouted jobs

      // Modal Visibility
      showScoutConfirmation: false,
      errorMessage: '',
      loading: false,
      // When true, hides this popup so the job search page underneath shows instead.
      loadFailed: false,
    };
  },
  computed: {
    ...mapGetters({
      profile: 'getProfile',
      companies: 'getCompanies',
    }),
    selectedCompany() {
      const companyId = this.currentJob && (this.currentJob.company_id || this.currentJob.companyId);
      if (!companyId || !this.companies || !this.companies.length) return null;
      return this.companies.find(c => c._id === companyId) || null;
    },
    company() {
      return this.profile.company_name;
    },
    isBaseModalVisible() {
      return this.currentState === "scoutSuccess" || this.currentState === "final";
    },

    isJobModalVisible() {
      return this.currentState === 'jobs' && !this.loadFailed;
    },

    modalId() {
      return "finalModal";
    },

    modalText() {
      if (this.currentState === "scoutSuccess") {
        return "スカウト希望が完了しました！";
      }
      return "あなたに合う求人の閲覧が完了しました！<br>その他の求人や、<br/>企業イベント情報を閲覧できます";
    },

    buttonText() {
      return "OK";
    },

    // Job related computations
    currentJob() {
      return this.jobs[this.currentJobIndex];
    },

    isLastJob() {
      return this.currentJobIndex === this.jobs.length - 1;
    },

    // Scout status computations
    isCurrentJobScouted() {
      if (!this.currentJob) return false;
      return this.isJobScoutedById(this.currentJob._id);
    },

    hasReachedScoutLimit() {
      if (this.scoutedJobs.length >= 10) return true;
      // Check if there's already a scouted job from the same company as current job
      if (!this.currentJob) return false;
      const currentCompanyId = this.currentJob.company_id || this.currentJob.companyId;
      if (!currentCompanyId) return false;
      return this.scoutedJobs.some(job => {
        const jobCompanyId = job.company_id || job.companyId;
        return jobCompanyId && String(jobCompanyId) === String(currentCompanyId);
      });
    },

    hasCompanyActiveScout() {
      // Check if there's already a scouted job from the same company as current job
      if (!this.currentJob || !this.scoutedJobs.length) return false;
      const currentCompanyId = this.currentJob.company_id || this.currentJob.companyId;
      if (!currentCompanyId) return false;
      return this.scoutedJobs.some(job => {
        const jobCompanyId = job.company_id || job.companyId;
        return jobCompanyId && String(jobCompanyId) === String(currentCompanyId);
      });
    },
  },
  methods: {
    async getTopThreeJobs() {
      let loadingTimeout;

      try {
        this.loading = true;
        
        // Set timeout to check loading state after 1 minute
        loadingTimeout = setTimeout(() => {
          if (this.loading) {
            this.loading = false;
            this.loadFailed = true;
          }
        }, 60000);

        await this.$auth.getToken();
        const jobs = await this.axios({
          method: "get",
          url: Api.url(Api.endpoints.initialJobs),
        });

        if (!jobs.data || jobs.data.length === 0) {
          this.loadFailed = true;
          return;
        }

        this.jobs = jobs.data.slice(0, 3);
      } catch (err) {
        console.error("Error fetching jobs:", err);
        this.loadFailed = true;
      } finally {
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
        }
        this.loading = false;
      }
    },
    markScoutPopupViewed() {
      localStorage.setItem('hasViewedScoutPopup', 'true');
    },
    // Base Modal Actions
    handleBaseModalAction() {
      if (this.currentState === "scoutSuccess") {
        this.currentState = "final";
        return;
      }
      if (this.currentState === "final") {
        this.markScoutPopupViewed();
        if (this.$refs.baseModal) {
          this.$refs.baseModal.hide();
        }
        this.currentState = "done";
      }
    },

    closeModal() {
      this.$refs.baseModal.hide();
      if (this.modalId === 'finalModal') {
        this.markScoutPopupViewed();
      }
    },

    handlePopupClose() {
      this.errorMessage = '';
      this.markScoutPopupViewed();
    },



    // Job Modal Actions
    handleNextJob() {
      if (this.isLastJob) {
        this.showFinalState();
      } else {
        this.currentJobIndex++;
      }
    },
    handlePrevJob() {
      if (this.currentJobIndex > 0) {
        this.currentJobIndex--;
      }
    },

    showFinalState() {
      this.currentState = "final";
    },

    // Scout Confirmation Actions
    openScoutConfirmation() {
      this.showScoutConfirmation = true;
      this.$nextTick(() => {
        this.$refs.scoutConfirmModal.show();
      });
    },

    handleScoutConfirm() {
      const companyId = this.currentJob.company_id || this.currentJob.companyId;
      const registered = (this.profile.other_companies || []).map(String);
      if (registered.includes(String(companyId))) {
        return this.scoutCurrentJob();
      }
      // Non-registered company: registration required before scouting
      this.showScoutConfirmation = false;
      this.$nextTick(() => this.$refs.companySelectionModal.show());
    },

    handleCompanySelectionProceed() {
      this.$refs.termsConditionsModal.show();
    },

    handleCompanySelectionCancel() {
      this.$refs.companySelectionModal.hide();
      this.$nextTick(() => this.$refs.jobModal && this.$refs.jobModal.show());
    },

    handleTermsAgree() {
      this.scoutCurrentJob(this.currentJob.company_id || this.currentJob.companyId);
    },

    handleTermsCancel() {
      this.$refs.termsConditionsModal.hide();
    },

    async scoutCurrentJob(companyToShare = null) {
      try {
        this.loading = true;
        await this.submitScoutRequest(this.currentJob, companyToShare);
        this.scoutedJobs.push(this.currentJob);
        // JobLists.vue (rendered as a sibling underneath this popup) tracks its own
        // scouted list / ticket count and has no other way to know a scout happened here.
        this.$emit('scouted');
        this.showScoutConfirmation = false;
        this.$refs.companySelectionModal && this.$refs.companySelectionModal.hide();
        if (this.isLastJob) {
          this.markScoutPopupViewed();
        }
        this.currentState = "scoutSuccess";
      } catch (error) {
        console.error("Scout request failed:", error);
      } finally {
        this.loading = false;
      }
    },

    handleScoutCancel() {
      this.showScoutConfirmation = false;
      this.$nextTick(() => {
        this.$refs.jobModal.show();
      });
    },

    // Scout status methods
    isJobScoutedById(jobId) {
      return this.scoutedJobs.some(job => job._id === jobId);
    },

    // API Methods
    async submitScoutRequest(job, companyToShare = null) {
      await this.$auth.getToken();
      await this.axios({
        method: "post",
        url: Api.url(Api.endpoints.addToScout),
        data: {
          jobId: job._id,
          jobCompany: job.company_id,
          companyToShare,
        },
      });
    },
  },
  mounted() {
    // Show modal immediately with loader, then load jobs
    this.$nextTick(() => {
      this.$refs.jobModal.show();
      this.getTopThreeJobs();
    });
  },
  watch: {
    isBaseModalVisible(visible) {
      if (visible) {
        this.$nextTick(() => {
          if (this.$refs.baseModal) {
            this.$refs.baseModal.show();
          }
        });
      }
    },
    isJobModalVisible(newValue) {
      if (newValue) {
        this.getTopThreeJobs();
      }
    },
  },
};
</script>
